import { useState } from 'react'

import type { SendWebhookResponseI } from '../../hooks/useSendWebhook'
import { useSendWebhook } from '../../hooks/useSendWebhook'
import { useLightMode } from '@/hooks/useLightMode'
import { useTranslation } from 'react-i18next'

import { Button, Flex, Modal, Select, Stack, Text } from '@hivewatch/dark'
import type { SelectOption } from '@hivewatch/dark'

import { useWebhookOptionsQuery } from '@/graphql/generated/hooks'
import { tVar } from '@/utils/i18n'

interface SendWebhookModalIProps {
  incidentId: string
  isOpen: boolean
  onClose: () => void
}

/**
 * Picks one of the webhooks configured in Settings > Webhooks and sends the
 * current incident through it (ENG-36019). The modal deliberately does not
 * close on submit — the destination's raw response (status and body) renders
 * at the bottom, unparsed, so the user can read it or send again. Available
 * to any role at any point in the incident's life cycle; gating is the
 * manualWebhooks flag alone, enforced at the menu item that opens this.
 */
export const SendWebhookModal = ({
  incidentId,
  isOpen,
  onClose,
}: SendWebhookModalIProps) => {
  const { t } = useTranslation()
  const colorScheme = useLightMode()
  // Light-mode tokens live under a `light.` namespace inside DarkProvider.
  const prefix = colorScheme === 'light' ? 'light.' : ''

  const [selected, setSelected] = useState<SelectOption | null>(null)
  const [response, setResponse] = useState<SendWebhookResponseI | null>(null)
  const [hasSendFailed, setHasSendFailed] = useState(false)

  const { send, isSending } = useSendWebhook(incidentId)

  // Slim id/name query — the picker must not pull auth-header secrets or
  // saved payloads into the drawer's cache. cache-and-network so a webhook
  // just created in Settings shows up without a reload (Webhook:<id> entities
  // are shared in the cache); skip keeps the flag-off / modal-closed cost at
  // zero requests.
  const {
    data,
    error: webhooksError,
    loading: isLoadingWebhooks,
    refetch: refetchWebhooks,
  } = useWebhookOptionsQuery({
    fetchPolicy: 'cache-and-network',
    skip: !isOpen,
  })
  const webhookOptions: SelectOption[] =
    data?.webhooks?.edges?.flatMap((edge) =>
      edge?.node ? [{ label: edge.node.name, value: edge.node.id }] : []
    ) ?? []
  // A failed load with nothing cached must not read as "No webhooks
  // configured" — that message is reserved for a successful empty result.
  const hasLoadFailed = Boolean(webhooksError) && !data

  const close = () => {
    // Reset so reopening starts fresh — no stale selection or response.
    setSelected(null)
    setResponse(null)
    setHasSendFailed(false)
    onClose()
  }

  const handleSend = async () => {
    if (!selected) return
    const result = await send(selected.value)
    setResponse(result)
    setHasSendFailed(!result)
  }

  return (
    <Modal
      closeOnEsc={!isSending}
      closeOnOverlayClick={!isSending}
      colorScheme={colorScheme}
      isCloseHidden={isSending}
      isOpen={isOpen}
      isSaveDisabled={!selected}
      isSaveLoading={isSending}
      onClose={close}
      onSave={handleSend}
      saveButtonLabel={t('incidents:send_webhook_send')}
      showCancelButton={!isSending}
      size='compact'
      title={t('incidents:send_webhook_title')}
      zIndex={1500}
    >
      <Stack gap='16px'>
        <Select
          colorScheme={colorScheme}
          data-testid='SendWebhookModal:webhook'
          inputId='send-webhook-picker'
          isDisabled={isSending}
          isLoading={isLoadingWebhooks}
          label={t('incidents:send_webhook_picker_label')}
          noOptionsMessage={() =>
            hasLoadFailed
              ? t('incidents:send_webhook_load_error')
              : t('incidents:send_webhook_empty')
          }
          onChange={(option) => {
            // Clear the previous result so a stale response can't read as
            // belonging to the newly picked webhook.
            setSelected(option)
            setResponse(null)
            setHasSendFailed(false)
          }}
          options={webhookOptions}
          placeholder={t('incidents:send_webhook_picker_placeholder')}
          value={selected}
        />
        {hasLoadFailed && (
          <Flex alignItems='center' gap='12px'>
            <Text color={`${prefix}text.alert-danger`} textStyle='caption'>
              {t('incidents:send_webhook_load_error')}
            </Text>
            <Button
              colorScheme={colorScheme}
              data-testid='SendWebhookModal:retry'
              onClick={() => {
                // A rejected refetch is already surfaced through the hook's
                // error state, which keeps this row on screen.
                refetchWebhooks().catch(() => undefined)
              }}
              size='sm'
              variant='secondary'
            >
              {t('common:retry')}
            </Button>
          </Flex>
        )}
        {(response || hasSendFailed) && (
          <Stack
            border='1px solid'
            borderColor={`${prefix}border.subtle`}
            borderRadius='8px'
            data-testid='SendWebhookModal:response'
            gap='8px'
            maxH='240px'
            overflowY='auto'
            p='12px'
          >
            <Text color={`${prefix}text.secondary`} textStyle='caption'>
              {t('incidents:send_webhook_response_label')}
            </Text>
            {response ? (
              <>
                <Text fontFamily='monospace' textStyle='caption'>
                  {tVar(t, 'incidents:send_webhook_response_status', {
                    status: response.statusCode,
                  })}
                </Text>
                {Boolean(response.body) && (
                  <Text
                    fontFamily='monospace'
                    textStyle='caption'
                    whiteSpace='pre-wrap'
                    wordBreak='break-word'
                  >
                    {response.body}
                  </Text>
                )}
              </>
            ) : (
              <Text color={`${prefix}text.alert-danger`} textStyle='caption'>
                {t('incidents:send_webhook_error')}
              </Text>
            )}
          </Stack>
        )}
      </Stack>
    </Modal>
  )
}
